import { type FC, useCallback } from 'react';

import { type OddsFormat } from '@cx-modules/types';
import { OddsButton } from '@cx-modules/ui';
import { sendOddsClickCustomEvent } from '@cx-modules/utils';

import { roundOdds } from '../../../../helpers';
import { useBetslip, useOutrightsCouponContext } from '../../../../hooks';
import { type LoadOutrightsCouponArgs } from '../../../../types';
import { type MarketBet } from '../../../_common';

import { BetOddButtonWrapper } from './style';

const getOddText = (oddsFormat: OddsFormat, { bestOddsAmerican, bestOddsDecimal, bestOddsFractional }: MarketBet) => {
  switch (oddsFormat) {
    case 'american':
      return bestOddsAmerican || 'SP';
    case 'decimal':
      return bestOddsDecimal ? roundOdds(bestOddsDecimal) : 'SP';
    case 'fractional':
      return bestOddsFractional || 'SP';
    default:
      return 'SP';
  }
};

interface BetOddButtonProps {
  bet: MarketBet;
  marketName?: string;
  newUI?: boolean;
  oddsShown: LoadOutrightsCouponArgs['oddsShown'];
  subEventName?: string;
}

export const BetOddButton: FC<BetOddButtonProps> = ({ bet, marketName, newUI, oddsShown, subEventName }) => {
  const oddsFormat = useOutrightsCouponContext(state => state.user.oddsFormat);
  const renderBadge = useOutrightsCouponContext(state => state.renderOddsButtonBadge);
  const bookmakersConfig = useOutrightsCouponContext(state => state.bookmakersConfig);

  const { bestOddsBookmakerCodes, betId } = bet;
  const isNonClickable = oddsShown === 'OC_NON_CLICKABLE';

  // TODO: calc correct bookieCode from best odds [bestOdd.bookmakerCode]
  // rather than selecting first bookie from list...
  const defaultBookieCode = bestOddsBookmakerCodes[0];
  const buttonBookieCode = isNonClickable ? 'OC_NON_CLICKABLE' : defaultBookieCode;
  const bookmakerConfig = bookmakersConfig[buttonBookieCode] ?? bookmakersConfig[defaultBookieCode];

  const { isBookieBetSelected: isBetSelected, toggleBetslipBet } = useBetslip({
    betId,
    betLocation: 'outrights-odds-button',
    betName: bet.betName,
    betOdds: bet,
    bookieCode: defaultBookieCode, // Betslip logic might still need default bookie?
    eventName: subEventName,
    marketName,
  });

  const oddsText = getOddText(oddsFormat, bet);

  const toggleBetslipBetAndSendTrackingEvent = useCallback(() => {
    // Only send event if clickable
    if (isNonClickable) return;

    sendOddsClickCustomEvent({
      betLocation: 'outrights-odds-button',
      betName: bet.betName,
      betOdds: [bet],
      bookieCode: defaultBookieCode,
      event: 'odds_button_click',
      eventName: subEventName,
      marketName,
      oddsFormat,
      pageTitle: 'football-competition',
    });
    toggleBetslipBet();
  }, [bet, defaultBookieCode, subEventName, isNonClickable, marketName, oddsFormat, toggleBetslipBet]);

  return (
    <BetOddButtonWrapper>
      <OddsButton
        bookieCode={buttonBookieCode}
        bookmakerConfig={bookmakerConfig}
        disabled={isNonClickable}
        isSelected={!isNonClickable && isBetSelected}
        isVerticalBadgeLogo={true}
        newUI={newUI}
        onClick={toggleBetslipBetAndSendTrackingEvent}
        renderBadge={renderBadge}
      >
        {oddsText}
      </OddsButton>
    </BetOddButtonWrapper>
  );
};
